/* Base button */
.media-minimal-skin .media-button {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  height: calc(var(--media-spacing) * 9.5);
  min-height: 0;
  padding: calc(var(--media-spacing) * 2) calc(var(--media-spacing) * 4);
  text-align: center;
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  outline: 2px solid transparent;
  outline-offset: -2px;
  border: none;
  border-radius: calc(var(--media-spacing) * 2);
  transition-timing-function: ease-out;
  transition-duration: 150ms;
  transition-property: background-color, outline-offset, scale;
  /* Fix weird jumping when clicking on the buttons in Safari. */
  will-change: scale;

  &:focus-visible {
    outline-color: var(--media-focus-ring-color);
    outline-offset: 2px;
  }

  &:active:not([aria-disabled="true"]) {
    scale: 0.97;
  }

  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

@supports (corner-shape: squircle) {
  .media-minimal-skin .media-button {
    border-radius: calc(var(--media-spacing) * 4);
    corner-shape: squircle;
  }
}

/* Primary button variant */
.media-minimal-skin .media-button--primary {
  font-weight: 500;
  color: var(--media-accent-contrast-color);
  text-shadow: none;
  background: var(--media-internal-accent-color);
}

/* Subtle button variant */
.media-minimal-skin .media-button--subtle {
  color: inherit;
  text-shadow: inherit;
  background: transparent;

  &:not([aria-disabled="true"]) {
    &:hover,
    &:focus-visible,
    &[aria-expanded="true"] {
      color: var(--media-internal-accent-text-color);
      text-decoration: none;
      background-color: var(--media-accent-background-color);
    }
  }
}

/* Icon button variant */
.media-minimal-skin .media-button--icon {
  display: grid;
  aspect-ratio: 1;
  padding: 0;

  &:active:not([aria-disabled="true"]) {
    scale: 0.97;
  }

  .media-icon__container {
    display: grid;
  }

  .media-icon {
    grid-area: 1 / 1;
    filter: drop-shadow(0 1px 0 var(--media-shadow-current-color));
    transition-timing-function: ease-out;
    transition-duration: 150ms;
    transition-property: opacity, scale;
  }
}

/* Seek button */
.media-minimal-skin .media-button--seek {
  .media-icon__label {
    position: absolute;
    right: -1px;
    bottom: -3px;
    font-size: 0.715em; /* 10px */
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.05em;
  }

  &:has(.media-icon--flipped) .media-icon__label {
    right: unset;
    left: -1px;
  }
}

/* Playback rate button */
.media-minimal-skin .media-button--playback-rate {
  padding: 0;
  font-variant-numeric: tabular-nums;

  &::after {
    width: 4ch;
    content: attr(data-rate) "\00D7";
  }

  &[data-inline-rate-label]::after {
    content: none;
  }
}

/* Settings button */
.media-minimal-skin .media-button--settings {
  .media-icon--settings {
    transition: transform 150ms ease-in-out;

    @media (prefers-reduced-motion: reduce) {
      transition-duration: 0ms;
    }
  }

  &[aria-expanded="true"] .media-icon--settings {
    transform: rotate(90deg);
  }
}

/* Live button — wide pill button with a status dot (gray → red at the live
   edge) rendered via ::before, and "LIVE" text rendered as the button's own
   text content. */
.media-minimal-skin .media-button--live {
  display: inline-flex;
  gap: calc(var(--media-spacing) * 1.5);
  align-items: center;
  width: auto;
  aspect-ratio: auto;
  padding: calc(var(--media-spacing) * 2) calc(var(--media-spacing) * 3);
  font-size: var(--media-font-size-small);
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &::before {
    display: inline-block;
    flex-shrink: 0;
    width: calc(var(--media-spacing) * 2);
    height: calc(var(--media-spacing) * 2);
    content: "";
    background-color: oklch(from currentColor l c h / 0.4);
    border-radius: 50%;
    transition: background-color 150ms ease-out;
  }

  &[data-live-edge]::before {
    background-color: oklch(0.65 0.22 27);
  }
}

@media (prefers-reduced-motion: reduce) {
  .media-minimal-skin .media-button {
    scale: 1;
    transition-property: background-color;
    will-change: auto;
  }
}
